import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser, SignInButton } from "../hooks/useAuthCompat";
import { useApi } from "../hooks/useApi";
import { CheckCircle2, BookOpen, NotebookPen, ArrowRight } from "lucide-react";

const ACT_IMAGES = { 1: "/tms/act1.jpg", 2: "/tms/act2.jpg", 3: "/tms/act3.jpg", 4: "/tms/act4.jpg", 5: "/tms/act5.jpg", 6: "/tms/act6.jpg", 7: "/tms/act7.jpg" };

export default function TMSDashboard() {
  const api = useApi();
  const { isSignedIn, isLoaded } = useUser();

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ["tms-dashboard"],
    queryFn: async () => (await api.get("/school/dashboard")).data,
    enabled: !!isSignedIn,
  });
  const { data: reflections } = useQuery({
    queryKey: ["tms-reflections"],
    queryFn: async () => (await api.get("/school/reflections")).data,
    enabled: !!isSignedIn,
  });

  if (!isLoaded) return <div className="theme-school min-h-screen" />;

  if (!isSignedIn) {
    return (
      <div className="theme-school min-h-screen flex items-center justify-center text-center px-6" data-testid="tms-dashboard-signin-required">
        <div>
          <img src="/tms/seal.png" alt="" className="w-14 h-14 rounded-full object-cover border border-primary/20 mx-auto mb-5" />
          <p className="text-textSecondary mb-4">Sign in to view your Muscle School dashboard.</p>
          <SignInButton>
            <button className="bg-primary text-primary-foreground px-6 py-3 text-sm uppercase tracking-wide">Sign In</button>
          </SignInButton>
        </div>
      </div>
    );
  }

  if (!isLoading && !dashboard?.enrolled) {
    return (
      <div className="theme-school min-h-screen flex items-center justify-center text-center px-6" data-testid="tms-dashboard-not-enrolled">
        <div>
          <h1 className="font-display text-display-sm mb-4">You're not enrolled yet</h1>
          <p className="text-textSecondary mb-6">Enroll in The Muscle School to unlock your dashboard and progress tracking.</p>
          <Link to="/themuscleschool" className="bg-primary text-primary-foreground px-6 py-3 text-sm uppercase tracking-wide inline-block">Go to The Muscle School</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-school min-h-screen" data-testid="tms-dashboard-page">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-16">
        <div className="flex items-center gap-3 mb-2">
          <img src="/tms/seal.png" alt="" className="w-9 h-9 rounded-full object-cover border border-primary/20" />
          <p className="font-meta text-xs uppercase tracking-[0.2em] text-primary">Your Dashboard</p>
        </div>
        <h1 className="font-display text-display-md font-medium mb-10">The Muscle School</h1>

        {isLoading ? (
          <p className="text-textSecondary">Loading your progress…</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
              <div data-testid="tms-dashboard-stat-completed" className="flex flex-col p-6 bg-card border border-border/60 hover:border-primary/40 transition-all duration-300">
                <p className="font-meta text-xs uppercase tracking-widest text-textSecondary mb-3">Lessons Completed</p>
                <p className="font-display text-2xl md:text-3xl text-primary mt-auto flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> {dashboard.completed_count} / {dashboard.total_lessons}
                </p>
              </div>
              <div data-testid="tms-dashboard-stat-reflections" className="flex flex-col p-6 bg-card border border-border/60 hover:border-primary/40 transition-all duration-300">
                <p className="font-meta text-xs uppercase tracking-widest text-textSecondary mb-3">Reflections Saved</p>
                <p className="font-display text-2xl md:text-3xl text-primary mt-auto flex items-center gap-2">
                  <NotebookPen className="w-5 h-5" /> {dashboard.reflections_count}
                </p>
              </div>
              <div data-testid="tms-dashboard-stat-progress" className="flex flex-col p-6 bg-card border border-border/60 hover:border-primary/40 transition-all duration-300">
                <p className="font-meta text-xs uppercase tracking-widest text-textSecondary mb-3">Curriculum Progress</p>
                <p className="font-display text-2xl md:text-3xl text-primary mt-auto flex items-center gap-2">
                  <BookOpen className="w-5 h-5" /> {dashboard.total_lessons ? Math.round((dashboard.completed_count / dashboard.total_lessons) * 100) : 0}%
                </p>
              </div>
            </div>

            {dashboard.resume && (
              <Link
                to={`/themuscleschool/lessons/${dashboard.resume.lesson.id}`}
                data-testid="tms-dashboard-resume-card"
                className="flex items-center justify-between gap-6 border border-primary/30 bg-card p-7 mb-12 hover:border-primary/60 transition-colors duration-200"
              >
                <div>
                  <p className="font-meta text-xs uppercase tracking-wide text-textSecondary mb-1">
                    {dashboard.resume.completed ? "Continue with" : "Resume where you left off"}
                  </p>
                  <h2 className="text-lg font-subtitle">{dashboard.resume.lesson.title}</h2>
                  <p className="font-meta text-xs text-textSecondary mt-1">{dashboard.resume.act_title} · {dashboard.resume.module_title}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-primary shrink-0" />
              </Link>
            )}

            <p className="font-meta text-xs uppercase tracking-widest text-textSecondary mb-5">Act-by-Act Progress</p>
            <div data-testid="tms-dashboard-acts-progress" className="flex flex-col gap-4 mb-14">
              {dashboard.acts_progress?.map((a) => (
                <div key={a.act_id} data-testid={`tms-act-progress-${a.number}`} className="flex items-center gap-5 md:gap-8 p-5 border border-border/50">
                  <img src={ACT_IMAGES[a.number]} alt="" className="w-14 h-14 md:w-16 md:h-16 rounded-full border border-primary/20 object-cover shrink-0" />
                  <div className="flex-1">
                    <p className="font-display text-sm md:text-base">Act {a.number} — {a.title}</p>
                    <p className="font-meta text-xs text-textSecondary uppercase tracking-wider mt-1">{a.completed} / {a.total} lessons complete</p>
                    <div className="h-1.5 w-full bg-secondary overflow-hidden mt-3">
                      <div className="h-full bg-brandAccent transition-all duration-700" style={{ width: `${a.total ? (a.completed / a.total) * 100 : 0}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="font-meta text-xs uppercase tracking-widest text-textSecondary mb-5">Your Reflections</p>
            {reflections?.length ? (
              <div className="space-y-4">
                {reflections.map((r) => (
                  <div key={r.lesson_id} data-testid={`tms-reflection-${r.lesson_id}`} className="border border-border bg-card p-6">
                    <div className="flex items-center justify-between mb-2">
                      <Link to={`/themuscleschool/lessons/${r.lesson_id}`} className="text-sm font-subtitle hover:text-primary">{r.lesson_title}</Link>
                      <span className="font-meta text-[11px] text-textSecondary">{new Date(r.updated_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-textSecondary leading-[1.6] font-body">{r.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-textSecondary text-sm" data-testid="tms-dashboard-no-reflections">No reflections yet — write one from any lesson page.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
